import { FieldTypes } from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { UpdateCampaignFormStep03ExternalValues } from '@features/campaigns/builder/containers/FormPages/FormPages.types';
import {
  UpdateExternalCampaignStep03CTVPayload,
  AdProductVideoSelfServicePayload,
  UploadVideoCreativesPayload,
  RestoredStep03ExternalData,
  Step03ExternalFormData,
  AdProductDisplaySelfServicePayload,
} from '@features/campaigns/builder/containers/FormPages/Step03External/Step03External.types.ts';
import { Creative, VideoCreativeUploadParams } from '@features/campaigns/builder/services/campaign/uploadCreatives.ts';
import {
  CampaignDetails,
  CampaignType,
  CREATIVE_FILE_TYPES,
  CreativeTypes,
  DisplayAdProduct,
} from '@features/campaigns/shared/types/campaign.ts';
import {
  isAdProductDisplayNonNull,
  isAdProductVideoCreativeExistsInDB,
  isAdProductVideoNonNull,
  isAdProductVideoSelfService,
} from '@features/campaigns/shared/types/typeGuards.ts';
import { DataStatuses } from '@shared/types/common.ts';

function step03DisplayPayloadComposer(
  formValues: UpdateCampaignFormStep03ExternalValues,
  stepSavedData: Step03ExternalFormData | undefined,
): AdProductDisplaySelfServicePayload | undefined {
  const { formatTypeDisplay } = formValues;
  const initialFormatTypeDisplay = Boolean(stepSavedData?.formatTypeDisplay);

  if (!formatTypeDisplay && !initialFormatTypeDisplay) {
    return undefined;
  }

  if (initialFormatTypeDisplay && !formatTypeDisplay) {
    return {};
  }

  return {
    type: DisplayAdProduct.SELF_SERVICE,
    lshapeContainsPoliticalContent: formValues.lShapeContainsPoliticalContent,
    landingContainsPoliticalContent: formValues.landingContainsPoliticalContent,
    leftLShapeContainsPoliticalContent: formValues.switchInLeftContainsPoliticalContent,
    bottomLShapeContainsPoliticalContent: formValues.switchInBottomContainsPoliticalContent,
  };
}

function step03VideoPayloadComposer(
  formValues: UpdateCampaignFormStep03ExternalValues,
  campaignType: CampaignType,
  stepSavedData: Step03ExternalFormData | undefined,
): AdProductVideoSelfServicePayload | undefined {
  const { formatTypeVideo, trackingUrl } = formValues;
  const initialFormatTypeVideo = Boolean(stepSavedData?.formatTypeVideo);
  const trackingUrlValue = trackingUrl || '';

  if (!formatTypeVideo && !initialFormatTypeVideo) {
    return undefined;
  }

  if (initialFormatTypeVideo && !formatTypeVideo) {
    return {};
  }

  return {
    type: campaignType,
    trackingUrl: trackingUrlValue,
    containsPoliticalContent: formValues.videoContainsPoliticalContent,
  };
}

export function updateCampaignStep03ExternalDTO(
  formValues: UpdateCampaignFormStep03ExternalValues,
  campaignType: CampaignType,
  stepSavedData: Step03ExternalFormData | undefined,
): UpdateExternalCampaignStep03CTVPayload {
  const video = step03VideoPayloadComposer(formValues, campaignType, stepSavedData);
  const display = step03DisplayPayloadComposer(formValues, stepSavedData);

  return {
    display,
    video,
  };
}

interface UploadDisplayCreativesPayload {
  [CREATIVE_FILE_TYPES.LSHAPE]?: Creative;
  [CREATIVE_FILE_TYPES.LANDING]?: Creative;
  [CREATIVE_FILE_TYPES.SWITCH_IN_LEFT]?: Creative;
  [CREATIVE_FILE_TYPES.SWITCH_IN_BOTTOM]?: Creative;
}

function fileCanBeUploaded(file: File | 'string'): boolean {
  return file && typeof file !== 'string';
}
export function uploadDisplayCreativesDTO(
  formValues: UpdateCampaignFormStep03ExternalValues,
): UploadDisplayCreativesPayload {
  const { creativeImageLShaped, creativeImageLanding, creativeSwitchInLeft, creativeSwitchInBottom } = formValues;
  const uploadedLshapeImage = fileCanBeUploaded(creativeImageLShaped) ? creativeImageLShaped[0] : null;
  const uploadedLandingImage = fileCanBeUploaded(creativeImageLanding) ? creativeImageLanding[0] : null;
  const uploadedSwitchInLeftImage = fileCanBeUploaded(creativeSwitchInLeft) ? creativeSwitchInLeft[0] : null;
  const uploadedSwitchInBottomImage = fileCanBeUploaded(creativeSwitchInBottom) ? creativeSwitchInBottom[0] : null;

  const schema = {
    [CREATIVE_FILE_TYPES.LSHAPE]: {
      file: uploadedLshapeImage,
      containsPoliticalContent: formValues.lShapeContainsPoliticalContent,
    },
    [CREATIVE_FILE_TYPES.LANDING]: {
      file: uploadedLandingImage,
      containsPoliticalContent: formValues.landingContainsPoliticalContent,
    },
    [CREATIVE_FILE_TYPES.SWITCH_IN_LEFT]: {
      file: uploadedSwitchInLeftImage,
      containsPoliticalContent: formValues.switchInLeftContainsPoliticalContent,
    },
    [CREATIVE_FILE_TYPES.SWITCH_IN_BOTTOM]: {
      file: uploadedSwitchInBottomImage,
      containsPoliticalContent: formValues.switchInBottomContainsPoliticalContent,
    },
  };

  return Object.fromEntries(
    Object.entries(schema).filter(([, value]) => {
      return !!value.file;
    }),
  );
}

export function uploadVideoCreativesOnStorageDTO(
  formValues: UpdateCampaignFormStep03ExternalValues,
  uploadParams: VideoCreativeUploadParams,
): FormData {
  const { creativeVideoSpot } = formValues;
  const file: File = creativeVideoSpot[0];

  const formData = new FormData();

  // Add all fields from uploadParams.fields
  Object.entries(uploadParams.fields).forEach(([key, value]) => {
    formData.append(key, value);
  });

  // Add the file as the last field
  formData.append('file', file);

  return formData;
}

export function uploadVideoCreativesDTO(
  formValues: UpdateCampaignFormStep03ExternalValues,
  fileStorageId: string,
): UploadVideoCreativesPayload {
  const { creativeVideoSpot, videoContainsPoliticalContent } = formValues;

  return {
    fileStorageId,
    originalFilename: creativeVideoSpot[0].name,
    containsPoliticalContent: videoContainsPoliticalContent ?? false,
  };
}

export function restoreStep03ExternalDataDTO(data: CampaignDetails): RestoredStep03ExternalData {
  const isFormatTypeVideoAvailable = isAdProductVideoNonNull(data.adProductVideo);
  const isFormatTypeDisplayAvailable = isAdProductDisplayNonNull(data.adProductDisplay);

  if (!isFormatTypeVideoAvailable && !isFormatTypeDisplayAvailable) {
    return {
      formData: undefined,
      invalidSections: data.invalidSections,
      status: data.status,
      country: data.country,
      data: undefined,
    };
  }

  const lShapedImageUrl = isAdProductDisplayNonNull(data.adProductDisplay)
    ? data.adProductDisplay.lShapeImageUrl
    : null;

  const landingImageUrl = isAdProductDisplayNonNull(data.adProductDisplay)
    ? data.adProductDisplay.landingImageUrl
    : null;

  const switchInLeftImageUrl = isAdProductDisplayNonNull(data.adProductDisplay)
    ? data.adProductDisplay.leftLShapeImageUrl
    : null;

  const switchInBottomImageUrl = isAdProductDisplayNonNull(data.adProductDisplay)
    ? data.adProductDisplay.bottomLShapeImageUrl
    : null;

  const videoSpotUrl =
    isAdProductVideoCreativeExistsInDB(data.adProductVideo) && data.adProductVideo.creative
      ? data.adProductVideo.creative.url
      : null;

  const originalFilename =
    isAdProductVideoCreativeExistsInDB(data.adProductVideo) && data.adProductVideo.creative
      ? data.adProductVideo.creative.originalFilename
      : undefined;

  const videoSpotStatus =
    isAdProductVideoCreativeExistsInDB(data.adProductVideo) && data.adProductVideo.creative
      ? data.adProductVideo.creative.status
      : DataStatuses.PENDING;

  // Only AdProductVideoSelfService has trackingUrl property
  const trackingUrl = isAdProductVideoSelfService(data.adProductVideo)
    ? data.adProductVideo.data.trackingUrl
    : undefined;

  const videoContainsPoliticalContent = isAdProductVideoSelfService(data.adProductVideo)
    ? (data.adProductVideo.creative?.containsPoliticalContent ?? false)
    : false;

  const lShapeContainsPoliticalContent = isAdProductDisplayNonNull(data.adProductDisplay)
    ? Boolean(
        data.adProductDisplay.creatives?.find((creative) => creative.type === CreativeTypes.CAMPAIGN_L_SHAPE)
          ?.containsPoliticalContent,
      )
    : false;

  const landingContainsPoliticalContent = isAdProductDisplayNonNull(data.adProductDisplay)
    ? Boolean(
        data.adProductDisplay.creatives?.find((creative) => creative.type === CreativeTypes.CAMPAIGN_LANDING)
          ?.containsPoliticalContent,
      )
    : false;

  const switchInLeftContainsPoliticalContent = isAdProductDisplayNonNull(data.adProductDisplay)
    ? Boolean(
        data.adProductDisplay.creatives?.find((creative) => creative.type === CreativeTypes.CAMPAIGN_LEFT_L_SHAPE)
          ?.containsPoliticalContent,
      )
    : false;

  const switchInBottomContainsPoliticalContent = isAdProductDisplayNonNull(data.adProductDisplay)
    ? Boolean(
        data.adProductDisplay.creatives?.find((creative) => creative.type === CreativeTypes.CAMPAIGN_BOTTOM_L_SHAPE)
          ?.containsPoliticalContent,
      )
    : false;

  return {
    formData: {
      [FieldTypes.FORMAT_TYPE_VIDEO]: isFormatTypeVideoAvailable,
      [FieldTypes.FORMAT_TYPE_DISPLAY]: isFormatTypeDisplayAvailable,
      [FieldTypes.CREATIVE_L_SHAPED_IMAGE]: lShapedImageUrl,
      [FieldTypes.CREATIVE_LANDING_IMAGE]: landingImageUrl,
      [FieldTypes.CREATIVE_SWITCH_IN_LEFT]: switchInLeftImageUrl,
      [FieldTypes.CREATIVE_SWITCH_IN_BOTTOM]: switchInBottomImageUrl,
      [FieldTypes.CREATIVE_VIDEO_SPOT]: videoSpotUrl,
      [FieldTypes.CREATIVE_VIDEO_SPOT_STATUS]: videoSpotStatus,
      [FieldTypes.TRACKING_URL]: !trackingUrl ? undefined : trackingUrl,
      [FieldTypes.VIDEO_CONTAINS_POLITICAL_CONTENT]: videoContainsPoliticalContent,
      [FieldTypes.LSHAPE_CONTAINS_POLITICAL_CONTENT]: lShapeContainsPoliticalContent,
      [FieldTypes.LANDING_CONTAINS_POLITICAL_CONTENT]: landingContainsPoliticalContent,
      [FieldTypes.SWITCH_IN_LEFT_CONTAINS_POLITICAL_CONTENT]: switchInLeftContainsPoliticalContent,
      [FieldTypes.SWITCH_IN_BOTTOM_CONTAINS_POLITICAL_CONTENT]: switchInBottomContainsPoliticalContent,
    },
    invalidSections: data.invalidSections,
    status: data.status,
    country: data.country,
    data: {
      originalFilename,
    },
  };
}
